import clsx from 'clsx';
import { motion } from 'framer-motion';
import Cookies from 'js-cookie';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useRecoilState } from 'recoil';

import { ShouldRender } from '@/components/shared/basic/ShouldRender';
import CustomLoader from '@/components/shared/Loading';

import SidePanel from './SidePanel';
import PaymentSuccessful from '../CartPage/PaymentSuccessful';
import { notify } from '../shared/basic/notify';
import { trackConversion, trackPurchaseCartEvent } from '../shared/Gtags';
import { cartAtom } from '../../atoms/Cart';
import { hasNewBookAtom } from '../../atoms/hasNewBook';
import { user } from '../../atoms/user';
import { useAddresses } from '../../hooks/useAddresses';
import { Endpoints } from '../../network';
import { generateUrlForImage } from '../../pages/books/[bookId]';
import { getOrderDetails } from '../../services/Cart';
import { getAllBookListApi, getSyncBookListApi } from '../../services/Library/Books';
import { getUserProfile } from '../../services/user';
import {
  FormattedFinalOrder,
  RawOrderData,
} from '../../utils/interfaces/Orders';

const cardVariants = {
  hidden: { opacity: 0, x: 100 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { type: 'tween', duration: 0.5 },
  },
};

const right = {
  hidden: { opacity: 0, x: -100 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { type: 'tween', duration: 0.5 },
  },
};

const ThankyouPage = () => {
  const router = useRouter();
  const [cartData, setCartData] = useRecoilState(cartAtom);
  const [order, setOrder] = useState<FormattedFinalOrder>();
  const [loadingOrder, setLoadingOrder] = useState(true);
  const [loadingBook, setLoadingBook] = useState(true);
  const hasAnimatedRef = useRef(false);

  const [books, setBooks] = useState<
    {
      _id: string;
      coverImageUrl: string;
      title: string;
      externalLink: string;
      type: string;
      slug: string;
      totalPages: number;
      isExternalLink: boolean;
    }[]
  >([]);
  const [userData, setUserData] = useRecoilState(user);
  const [, setHasNewBook] = useRecoilState<boolean>(hasNewBookAtom);
  const hasFetchedAddresses = useRef(false);
  const purchaseEventCalledRef = useRef(false);
  const { getAllAddresses } = useAddresses();
  const { orderId, callPurchased, stripe_status } = router.query;

  const formatOrderDetails = (
    data: RawOrderData | null,
  ): FormattedFinalOrder | null => {
    if (!data) return null;
    return {
      orderId: data.id.toString(),
      transactionId: data.payment_details?.transaction_id || '',
      grand_total: data.grand_total || 0,
      payment_status: data?.payment_status,
      payment_type: data.payment_type,
      currency: data?.currency_symbol || '₹',
      currency_code: data?.currency_code || 'INR',
      orderDetails: data.order_details.map((item) => ({
        id: item.id.toString(),
        name: item.product.name,
        display_name: item.product.display_name,
        quantity: item.quantity,
        product_id: item.product_id,
        price: item.product.main_price,
        image: item.product.thumbnail_image,
        currency_symbol: item?.product?.currency_symbol,
        book_type: item?.product?.book_type,
        category: item?.product?.category,
        discounted_price: item.discounted_price,
        actualPrice: item.product.stroked_price,
        coupon_code: item.coupon_code || '',
      })),
      shipping_address: data?.shipping_address
    };
  };

  const handleFinalCartDetails = useCallback(
    async (id: string) => {
      try {
        setLoadingOrder(true);
        if (id) {
          const data = await getOrderDetails(id);
          const formattedData = formatOrderDetails(data);

          const hasBookCategory = formattedData?.orderDetails.some(
            (item) =>
              item.category?.toLowerCase() === 'book' ||
              item.category?.toLowerCase() === 'long book',
          );

          if (hasBookCategory && formattedData?.payment_status === 'paid') {
            Cookies.set('hasNewBookInLibrary', 'true', { expires: 1 });
            setHasNewBook(true);
          }

          setOrder(formattedData as FormattedFinalOrder);
          if (cartData.length) setCartData([]);
        }

        const data = await getUserProfile();
        setUserData(data?.data);
      } catch (error: any) {
        if (process.env.NEXT_PUBLIC_APP_ENV === 'development') {
          console.error('Error initializing messaging:', error);
        }
      } finally {
        setLoadingOrder(false);
        hasAnimatedRef.current = true;
      }
    },
    [cartData],
  );

  const buyNitroEvent = async (orderId: number) => {
    try {
      if (!order) return;


      if (
        typeof (window as any).nitro !== 'undefined' &&
        typeof (window as any).nitro.buy === 'function') {
        const currentUrl = window.location.href;
        ;
        const zebra_selected_country = JSON?.parse(
          localStorage?.getItem?.('zebra_selected_country') as string,
        )?.value?.country;
        const buyData = {
          order_id: orderId,//
          checkout_url: currentUrl,//
          country: zebra_selected_country?.name,
          currency: zebra_selected_country?.currency,
          items: (order.orderDetails || []).map((item) => ({
            product_id: item?.product_id,
            title: item?.display_name,
            price: item?.price,
            currency: zebra_selected_country?.currency,
            product_url: `${process.env.NEXT_PUBLIC_API_URL_APP_URL}/books/temp-${item?.product_id}`
          })),
        };

        nitro.buy(buyData);
        ;
      }
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {

    if (stripe_status === 'true' && orderId && order) {

      getSyncBookListApi();
      if (typeof window !== 'undefined') {
        buyNitroEvent(parseInt((orderId as string)))
      }
    }

  }, [stripe_status, orderId, order])

  useEffect(() => {
    if (orderId) handleFinalCartDetails(orderId as string);
    if (!hasFetchedAddresses.current && userData) {
      getAllAddresses();
      hasFetchedAddresses.current = true;
    }
  }, [orderId, handleFinalCartDetails]);
  useEffect(() => {
    if (
      order &&
      orderId &&
      callPurchased &&
      !purchaseEventCalledRef.current &&
      userData
    ) {

      trackConversion({
        currency: order?.currency_code || 'INR',
        send_to: `${process.env.NEXT_PUBLIC_GOOGLE_ID_ADD}/${order.orderId}`,
        value: order.grand_total as number,
        transaction_id: order.orderId || '',
      });

      trackPurchaseCartEvent({
        purchaseData: {
          transaction_id: orderId as string,
          coupon: order?.orderDetails?.[0]?.coupon_code || '',
          currency: order?.currency_code || 'INR',
          productName: (order.orderDetails || [])
            .map((detail) => detail.name)
            .join(','),
          paymentType: order?.payment_type as string,
          items: (order.orderDetails || []).map((item) => ({
            item_id: item.product_id.toString(),
            item_name: item.name,
            coupon: order?.orderDetails?.[0]?.coupon_code || '',
            discount: (item.actualPrice || 0) - (item.price || 0),
            price: item.discounted_price || 0,
            quantity: item.quantity || 1,
            display_name: item.display_name,
            item_category: item.category,
          })),
          shipping: 'Free',
          tax: 0,
          value: order.grand_total as number,
        },
        customerData: {
          customerBillingCity: order?.shipping_address?.city as string,
          customerBillingState: order?.shipping_address?.state as string,
          customerBillingCountry: order?.shipping_address?.country as string,
          customerBillingEmail: userData?.email as string,
          customerBillingName: userData?.name as string,
          customerBillingPhone: userData?.phone as string,
          customerBillingPostalcode: order?.shipping_address?.postal_code as string,
        },
        userData,
        pathname: router.pathname,
        extraMetadata: {
          inr_value: order?.inr_grand_total,
          state: order?.shipping_address?.state,
          postal_code: order?.shipping_address?.postal_code
        }
      });

      purchaseEventCalledRef.current = true;

      const { pathname, query } = router;
      delete query.callPurchased;
      router.replace({ pathname, query }, undefined, { shallow: true });
    }
  }, [orderId, callPurchased, order, router, userData]);

  const handleGetAllBooks = async () => {
    try {
      setLoadingBook(true);
      const response = await getAllBookListApi({
        page: '1',
        ecomProductId: router?.query?.product_ids?.includes(',')
          ? (router?.query?.product_ids as string)?.split(',')[0]
          : (router?.query?.product_ids as string),
        limit: '3',
      });
      const { data } = response;

      const formattedBooks = data?.map((book: any) => ({
        _id: book?._id,
        coverImageUrl: generateUrlForImage(
          `${process.env.NEXT_PUBLIC_API_READER_URL}/`,
          `${Endpoints.getCoverImagesForBooks}/`,
          `${book?._id}`,
        ),
        title: book?.title,
        externalLink: book?.externalLink,
        slug: book?.slug,
        type: book?.type,
        isExternalLink: book?.isExternalLink,
        totalPages: book?.totalPages,
      }));

      setBooks(formattedBooks);
    } catch (error: any) {
      notify(error, 'error');
    } finally {
      setLoadingBook(false);
    }
  };

  useEffect(() => {
    handleGetAllBooks();
  }, []);

  return (
    <div className='relative '>
      <div className='flex flex-col overflow-hidden lg:gap-y-5'>
        <div className='relative flex lg:flex-row flex-col'>
          <div className='lg:border-r-2 col-span-2 border-borderDarkGray flex-1'>
            <motion.div
              variants={right}
              initial='hidden'
              animate={hasAnimatedRef.current ? 'visible' : 'hidden'}
              className='flex flex-col h-full  sm:gap-[77px] gap-8'
            >
              <div className='h-full'>
                <PaymentSuccessful
                  order={order}
                  books={books}
                  loadingBook={loadingBook}
                />
              </div>

              <div className='flex lg:hidden '>
                <SidePanel
                  order={order as FormattedFinalOrder}
                  loadingOrder={loadingOrder}
                />
              </div>
            </motion.div>
          </div>

          <div className='2xl:max-w-[574px] xl:max-w-[470px] lg:max-w-[385px] w-full lg:min-h-[100vh] col-span-1 border-borderDarkGray'>
            <div className={clsx('', 'flex flex-col h-full')}>
              <motion.div
                variants={cardVariants}
                initial='hidden'
                animate={hasAnimatedRef.current ? 'visible' : 'hidden'}
                className='hidden lg:flex justify-center w-full '
              >
                <SidePanel
                  order={order as FormattedFinalOrder}
                  loadingOrder={loadingOrder}
                />
              </motion.div>
            </div>
          </div>
        </div>
      </div>
      <ShouldRender check={loadingOrder}>
        <CustomLoader />
      </ShouldRender>
    </div>
  );
};

export default ThankyouPage;
